<template>
  <div class="map-popup-content-root" @click.stop>
    <template v-if="popupType === 'defect'">
      <!-- 1) 식별 + 보조 액션 (한 줄) -->
      <div class="popup-defect-header d-flex align-center justify-space-between">
        <div class="popup-defect-id pa-0 min-width-0 text-truncate">
          <span class="popup-defect-id__label">{{ $t('defects.id') }}</span>
          <span class="popup-defect-id__value">DF-ID-{{ paddedDefectId }}</span>
        </div>
        <v-btn
          small
          outlined
          class="solvit-crud-outline-btn popup-action-btn flex-shrink-0 ml-2"
          :disabled="isEditorBusy"
          @click.stop="openDefectList"
        >
          {{ $t('defects.dialogListTitle') }}
        </v-btn>
      </div>

      <!-- 2) 메타 — 2열 그리드로 세로 길이 압축 -->
      <div class="popup-meta-block">
        <div class="popup-meta-row">
          <span class="popup-meta-label">Array</span>
          <span class="popup-meta-value">{{ defectProperties.array_id || '-' }}</span>
        </div>
        <div class="popup-meta-row">
          <span class="popup-meta-label">String</span>
          <span class="popup-meta-value">{{ defectProperties.string_id || '-' }}</span>
        </div>
        <div class="popup-meta-row">
          <span class="popup-meta-label">Module</span>
          <span class="popup-meta-value">{{ defectProperties.module_id || '-' }}</span>
        </div>
        <div class="popup-meta-row">
          <span class="popup-meta-label">{{ $t('defects.status') }}</span>
          <span class="popup-meta-value">{{ defectProperties.defect_status || '-' }}</span>
        </div>
        <div class="popup-meta-row popup-meta-row--full">
          <span class="popup-meta-label">{{ $t('defects.imageFile') }}</span>
          <span class="popup-meta-value popup-meta-value--file">{{
            defectProperties.img_name || '-'
          }}</span>
        </div>
      </div>

      <!-- 3) 유형 편집 — 상세유형은 메인 선택 + 하위 목록이 있을 때만 -->
      <div class="popup-edit-block">
        <v-row dense class="ma-0">
          <v-col :cols="showSubTypeSelect ? 6 : 12" class="py-0" :class="showSubTypeSelect ? 'pl-0 pr-1' : 'px-0'">
            <v-select
              v-model="selectedMainType"
              class="popup-defect-select"
              dense
              hide-details
              outlined
              :label="$t('defects.typeLabel')"
              :placeholder="$t('defects.typeLabel')"
              :items="mainDefectTypes"
              :item-text="localizedDefectTypeName"
              item-value="id"
              return-object
              :disabled="isEditorBusy"
              :loading="isInitializingEditor || isLoadingMainTypes"
              @change="onMainTypeChange"
            />
          </v-col>
          <v-col v-if="showSubTypeSelect" cols="6" class="py-0 pr-0 pl-1">
            <v-select
              v-model="selectedSubType"
              class="popup-defect-select"
              dense
              hide-details
              outlined
              :label="$t('defects.subTypeLabel')"
              :placeholder="$t('defects.subTypeLabel')"
              :items="subDefectTypes"
              :item-text="localizedDefectTypeName"
              item-value="id"
              :disabled="isEditorBusy"
              :loading="isLoadingSubTypes"
              return-object
            />
          </v-col>
        </v-row>
      </div>

      <!-- 4) 액션 — flex 균등 분할 (block 금지: width:100%가 행을 터뜨림) -->
      <div class="popup-action-row d-flex align-center">
        <v-btn
          small
          outlined
          class="solvit-crud-outline-btn solvit-crud-outline-btn--danger popup-action-btn"
          :loading="isDeleting"
          @click.stop="deleteDefect"
        >
          {{ $t('common.delete') }}
        </v-btn>
        <v-btn
          small
          outlined
          class="solvit-crud-outline-btn popup-action-btn"
          :disabled="isEditorBusy"
          @click.stop="openDetail"
        >
          {{ $t('common.viewDetails') }}
        </v-btn>
        <v-btn
          small
          outlined
          class="solvit-crud-outline-btn solvit-crud-outline-btn--primary popup-action-btn"
          :loading="isSaving"
          @click.stop="saveDefectType"
        >
          {{ $t('common.save') }}
        </v-btn>
      </div>
    </template>

    <template v-else-if="popupType === 'module'">
      <div class="module-popup-root" style="min-width: 240px; max-width: 340px; padding: 2px 0">
        <div class="d-flex align-center justify-space-between mb-1">
          <span class="font-weight-medium" style="font-size: 13px">
            {{ $t('defects.moduleDefectListTitle') }}
            <span class="text--secondary font-weight-regular">· {{ moduleSeq }}</span>
          </span>
          <v-chip
            x-small
            label
            color="primary"
            text-color="white"
            class="ml-2 flex-shrink-0"
            style="height: 18px; font-size: 11px"
          >
            {{ $t('common.countItems', { n: moduleDefects.length }) }}
          </v-chip>
        </div>
        <div class="text-caption text--secondary mb-1" style="font-size: 11px; line-height: 1.3">
          Array {{ moduleArrayId }}
          <template v-if="moduleStringId !== '-'"> · String {{ moduleStringId }}</template>
        </div>
        <v-divider class="my-1" />
        <div v-if="moduleDefects.length" class="module-defect-list" style="margin: 0 -4px">
          <div
            v-for="defect in moduleDefects"
            :key="defect.seq"
            class="module-defect-item d-flex align-center px-1"
            style="border-radius: 6px; cursor: pointer; min-height: 36px"
            @click="openModuleDefectDetail(defect)"
          >
            <img
              class="module-defect-icon flex-shrink-0 mr-2"
              :src="moduleDefectIconSrc(defect)"
              :alt="moduleDefectTypeLabel(defect)"
              width="20"
              height="20"
            />
            <div class="flex-grow-1" style="min-width: 0">
              <div
                class="d-flex align-center"
                style="font-size: 12px; font-weight: 500; line-height: 1.4"
              >
                <span class="text-truncate">{{ moduleDefectTypeLabel(defect) }}</span>
                <v-chip
                  v-if="defect.mlDefectConfirmed === 'Y'"
                  x-small
                  color="primary"
                  class="ml-1 flex-shrink-0"
                  style="height: 14px; font-size: 9px"
                >
                  AI
                </v-chip>
              </div>
              <div
                class="text-caption text--secondary text-truncate"
                style="font-size: 10px; line-height: 1.2"
              >
                <span>#{{ defect.seq }}</span>
                <span v-if="defect.imgName" class="ml-1">· {{ defect.imgName }}</span>
              </div>
            </div>
            <v-btn
              icon
              x-small
              class="flex-shrink-0 module-defect-delete-btn"
              :loading="deletingModuleDefectId === defect.seq"
              :disabled="isDeleting && deletingModuleDefectId !== defect.seq"
              @click.stop="deleteModuleDefect(defect)"
            >
              <v-icon x-small>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </div>
        <div v-else class="text-caption text--secondary py-3 text-center">
          {{ $t('defects.noDefectsFound') }}
        </div>
      </div>
    </template>

    <template v-else-if="popupType === 'rgb'">
      <h5>{{ defectProperties.img_name }}</h5>
    </template>
  </div>
</template>

<script>
  import api from '@/apis/Api';
  import { mapGetters } from 'vuex';
  import { defectTypeApi } from '../../apis/defect.type.api';
  import { moduleDefectApi } from '../../apis/module.defect.api';
  import {
    isDefectTypesCatalogActive,
    resolveCatalogWfsDefectSelection
  } from '../../constants/defectTypesCatalog';
  import {
    getModuleDefectDetailCached,
    setModuleDefectDetailCache
  } from '@/utils/moduleDefectDetailCache';
  import { findCellParentType, hasDefectCode } from '@/services/defect/defectCode.utils';
  import { optimisticDeleteDefect } from '@/features/defect/defectActions.js';
  import {
    getDefectTypeIconUrl,
    resolveDefectTypeDisplayName
  } from '@/utils/defect/defectTypeDisplay.js';
  import { resolveSolarModuleWfsProperties } from '@/utils/map/resolveSolarModuleWfsProperties.js';
  import { localizeDefectTypeLabel } from '@/utils/reportLocaleUtils';
  import { syncDefectModuleLinkFromServerLocation } from '@/services/defect/nearModuleResolver';

  /**
   * HAL 본문에 defectCode가 비어 있고 _embedded.defectCode만 있는 경우 상위로 올려
   * GET /moduleDefects/{id}/defectCode 보강 요청을 줄인다.
   */
  function mergeHalEmbeddedDefectCode(detail) {
    if (!detail || typeof detail !== 'object') {
      return detail;
    }
    const dc = detail.defectCode;
    if (dc && (dc.id != null || dc.seq != null)) {
      return detail;
    }
    const emb = detail._embedded?.defectCode;
    if (emb && typeof emb === 'object' && (emb.id != null || emb.seq != null)) {
      return { ...detail, defectCode: emb };
    }
    return detail;
  }

  /**
   * GET /module-defects/search/solarThermalImageId/{id} 목록의 한 건을 상세와 병합.
   * defectCode.parent 가 있어야 findByParent_Seq(= getChildrenDefectTypes)로 하위 유형을 맞출 수 있다.
   */
  function mergeListDefectWithDetail(detail, listDefect) {
    if (!listDefect || typeof listDefect !== 'object') {
      return detail;
    }
    const lc = listDefect.defectCode || listDefect._embedded?.defectCode;
    if (!lc || typeof lc !== 'object') {
      return detail;
    }
    const base = detail && typeof detail === 'object' ? { ...detail } : {};
    const existing = base.defectCode || {};
    base.defectCode = {
      ...existing,
      ...lc,
      parent: lc.parent != null ? lc.parent : existing.parent
    };
    return base;
  }

  function findDefectInThermalList(content, defectId) {
    if (!Array.isArray(content) || defectId == null) {
      return null;
    }
    const pid = Number(defectId);
    if (!Number.isFinite(pid)) {
      return null;
    }
    return (
      content.find(
        d =>
          d && (Number(d.id) === pid || Number(d.seq) === pid || Number(d.moduleDefectSeq) === pid)
      ) || null
    );
  }

  /**
   * Spring Data REST: 본문에 id/seq가 없고 PK는 _links.self(또는 moduleDefect) href에만 있음.
   */
  function extractModuleDefectPkFromHalLinks(detail) {
    if (!detail?._links || typeof detail._links !== 'object') {
      return null;
    }
    const href = detail._links.self?.href || detail._links.moduleDefect?.href;
    if (!href || typeof href !== 'string') {
      return null;
    }
    const m = href.match(/\/moduleDefects\/(\d+)/);
    if (!m) {
      return null;
    }
    const n = Number(m[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * REST로 받은 결함에는 defectShape(PanelDefect)가 없고 defectShapeGeom만 있는 경우가 많다.
   * 서버 persistModuleDefect()는 PanelDefect.defectShape가 필수라, DrawableImage.convertCoordsToRectDims와
   * 동일한 규칙으로 이미지 좌표 박스를 만든다.
   */
  function resolveDefectPkFromProps(props) {
    if (!props || typeof props !== 'object') {
      return null;
    }
    const candidates = [
      props.seq,
      props.id,
      props.defect_seq,
      props.module_defect_seq,
      props.moduleDefectSeq
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      const n = Number(candidates[i]);
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
    }
    return null;
  }

  function geomToPanelDefectShape(geom) {
    if (!geom?.coordinates) {
      return null;
    }
    const shapeType = geom.type;
    let ring = geom.coordinates;
    if (shapeType === 'Polygon') {
      ring = ring[0];
    }
    if (!Array.isArray(ring) || ring.length === 0) {
      return null;
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    ring.forEach(coords => {
      const x = coords[0];
      const y = coords[1];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    });
    if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
      return null;
    }
    return {
      left_x: Math.round(minX),
      top_y: Math.round(minY),
      width: Math.max(0, Math.round(maxX - minX)),
      height: Math.max(0, Math.round(maxY - minY))
    };
  }

  export default {
    name: 'DefectOverlayPopupContent',
    data() {
      return {
        /** DotSolarMapBox.loadDefectMarkers 와 동일: getParentDefectTypes() 결과 (Vuex와 무관하게 팝업에서 직접 로드) */
        mainDefectTypes: [],
        subDefectTypes: [],
        selectedMainType: null,
        selectedSubType: null,
        defectDetail: null,
        localDefectName: '',
        isInitializingEditor: false,
        isLoadingMainTypes: false,
        isLoadingSubTypes: false,
        isSaving: false,
        isDeleting: false,
        deletingModuleDefectId: null
      };
    },
    props: {
      defectProperties: {
        type: Object,
        required: true
      },
      popupType: {
        type: String,
        default: 'defect'
      }
    },
    computed: {
      ...mapGetters({
        thermalImage: 'defect/getThermalImage',
        reportId: 'explorer/getReportId',
        solarThermalImageId: 'defect/getSolarThermalImageId',
        defectTemperature: 'defect/getDefectTemperature',
        dashboardThermalImages: 'dashboard/getThermalImages',
        parentDefectTypes: 'defect/parentDefectTypes'
      }),
      wfsDefectRows() {
        return this.$store.state.dashboard?.wfsDefectRows || [];
      },
      resolvedSolarModule() {
        return resolveSolarModuleWfsProperties(this.defectProperties);
      },
      moduleSeq() {
        const { moduleId, solarModuleSeq } = this.resolvedSolarModule;
        return moduleId ?? solarModuleSeq ?? null;
      },
      moduleArrayId() {
        const id = this.resolvedSolarModule.arrayId;
        console.log('[MODULE-POPUP-DEBUG] moduleArrayId', {
          arrayId: id,
          defectPropsKeys: Object.keys(this.defectProperties || {}).filter(k => k !== 'geometry'),
          defectPropsArrayId: this.defectProperties?.array_id,
          resolvedSolarModule: this.resolvedSolarModule
        });
        return id ?? '-';
      },
      moduleStringId() {
        const id = this.resolvedSolarModule.stringId;
        console.log('[MODULE-POPUP-DEBUG] moduleStringId', {
          stringId: id,
          defectPropsStringId: this.defectProperties?.string_id
        });
        return id ?? '-';
      },
      moduleDefects() {
        const key = this.resolvedSolarModule.solarModuleSeq;
        if (key == null || key === '') {
          return [];
        }
        const moduleKey = String(key);
        return this.wfsDefectRows.filter(d => String(d.solarModuleSeq) === moduleKey);
      },
      resolvedDefectId() {
        return resolveDefectPkFromProps(this.defectProperties);
      },
      paddedDefectId() {
        const defectId = Number(this.resolvedDefectId || 0);
        return String(defectId).padStart(8, '0');
      },
      displayedDefectName() {
        return (
          this.localDefectName ||
          this.selectedSubType?.name ||
          this.selectedMainType?.name ||
          this.defectDetail?.defectCode?.name ||
          this.defectDetail?.defectType ||
          this.defectProperties.defect_name ||
          this.defectProperties.defectType ||
          '-'
        );
      },
      isEditorBusy() {
        return (
          this.isInitializingEditor ||
          this.isLoadingMainTypes ||
          this.isLoadingSubTypes ||
          this.isSaving ||
          this.isDeleting
        );
      },
      /** 메인 유형 선택 + 하위 목록(또는 로딩 중)일 때만 상세유형 노출 */
      showSubTypeSelect() {
        if (!this.selectedMainType) {
          return false;
        }
        if (this.isLoadingSubTypes) {
          return true;
        }
        return (this.subDefectTypes || []).length > 0;
      }
    },
    mounted() {
      if (this.popupType === 'defect') {
        this.initializeDefectEditor();
        this.runModuleListMappingDiag('defect-popup-mounted');
      }
      if (this.popupType === 'module') {
        this.logModulePopupDebug('mounted');
      }
    },
    watch: {
      wfsDefectRows: {
        handler() {
          if (this.popupType === 'module') {
            this.logModulePopupDebug('wfsDefectRows-changed');
          }
        },
        deep: true
      }
    },
    methods: {
      localizedDefectTypeName(item) {
        void this.$i18n.locale;
        return localizeDefectTypeLabel(
          key => this.$te(key),
          key => this.$t(key),
          item?.code,
          item?.name
        );
      },
      runModuleListMappingDiag(trigger) {
        if (typeof window === 'undefined') {
          return;
        }
        const diag = window.__solvitDefectModuleListDiag;
        if (typeof diag?.diagnoseFromWfsProps !== 'function') {
          console.warn(
            '[DEFECT-MODULE-LIST-DIAG] bridge not ready — DotSolarMapBox mounted 후 재시도'
          );
          return;
        }
        diag.diagnoseFromWfsProps(this.defectProperties, { trigger });
      },
      logModulePopupDebug(trigger) {
        const rows = this.wfsDefectRows || [];
        const resolved = this.resolvedSolarModule;
        const moduleKey = this.moduleSeq;
        const matched = this.moduleDefects;
        const unmatchedSample = rows
          .filter(d => String(d.solarModuleSeq) !== String(resolved.solarModuleSeq))
          .slice(0, 3)
          .map(d => ({ seq: d.seq, solarModuleSeq: d.solarModuleSeq }));
        console.log(`[MODULE-POPUP-DEBUG] DefectOverlayPopupContent (${trigger})`, {
          popupType: this.popupType,
          moduleSeq: moduleKey,
          solarModuleSeq: resolved.solarModuleSeq,
          moduleArrayId: this.moduleArrayId,
          moduleStringId: this.moduleStringId,
          defectPropertiesKeys: Object.keys(this.defectProperties || {}).filter(
            k => k !== 'geometry'
          ),
          defectProperties: {
            solar_module_seq: this.defectProperties?.solar_module_seq,
            seq: this.defectProperties?.seq,
            module_id: this.defectProperties?.module_id,
            array_id: this.defectProperties?.array_id,
            temp_array_id: this.defectProperties?.temp_array_id,
            string_id: this.defectProperties?.string_id,
            temp_string_id: this.defectProperties?.temp_string_id
          },
          resolvedSolarModule: resolved,
          wfsDefectRowCount: rows.length,
          matchedDefectCount: matched.length,
          matchedDefects: matched.map(d => ({
            seq: d.seq,
            solarModuleSeq: d.solarModuleSeq,
            defectType: d.defectType,
            defectStatus: d.defectStatus
          })),
          unmatchedSample,
          filterRule: `wfsDefectRows.solarModuleSeq === String(${resolved.solarModuleSeq})`
        });
      },
      moduleDefectTypeLabel(defect) {
        return (
          resolveDefectTypeDisplayName(defect, this.parentDefectTypes) ||
          defect?.defectType ||
          this.$t('defects.defect')
        );
      },
      moduleDefectIconSrc(defect) {
        return getDefectTypeIconUrl(defect);
      },
      openModuleDefectDetail(defect) {
        const defectId = Number(defect?.seq);
        if (!Number.isFinite(defectId) || defectId <= 0) {
          return;
        }
        this.$emit('open-detail', {
          defectId,
          thermalImgSeq: defect.thermalImgSeq
        });
      },
      async deleteModuleDefect(defect) {
        if (defect?.seq == null) {
          return;
        }
        const label = this.moduleDefectTypeLabel(defect);
        if (!window.confirm(`${label} (#${defect.seq}) ${this.$t('common.delete')}?`)) {
          return;
        }
        try {
          await optimisticDeleteDefect(this.$store, defect, {
            reportId: this.reportId,
            thermalImageId: defect.thermalImgSeq,
            eventBus: this.$eventBus,
            successMessage: this.$t('defects.popup.deleted')
          });
        } catch (error) {
          this.alertError(error);
        }
      },
      resolveThermalImageIdForPopup() {
        const seq = this.defectProperties?.thermal_img_seq;
        if (seq != null && seq !== '') {
          const n = Number(seq);
          if (Number.isFinite(n) && n > 0) {
            return n;
          }
        }
        const dash = this.$store.getters['dashboard/getSelectedThermalImage'];
        if (dash?.id != null) {
          const n = Number(dash.id);
          if (Number.isFinite(n) && n > 0) {
            return n;
          }
        }
        const st = this.solarThermalImageId;
        if (st != null && st !== '') {
          const n = Number(st);
          if (Number.isFinite(n) && n > 0) {
            return n;
          }
        }
        return null;
      },

      /**
       * 지도 팝업 「결함 리스트」: 왼쪽 목록·포커스와 무관하게 이 결함이 속한 열화상으로 연다.
       * 우선순위: WFS/상세에서 구한 thermal id(결함 소속) → 보고서 열화상 목록 매칭 → 최소 { id, imgName } → 마지막으로 대시보드 선택 열화상.
       */
      resolveThermalImageForOpenDefectList() {
        const thermalId = this.resolveThermalImageIdForPopup();
        if (thermalId != null && thermalId !== '') {
          const images = this.dashboardThermalImages || [];
          const fromList = images.find(img => img && Number(img.id) === Number(thermalId));
          if (fromList) {
            return fromList;
          }
          const detailSt = this.defectDetail?.solarThermalImage;
          if (
            detailSt &&
            typeof detailSt === 'object' &&
            detailSt.id != null &&
            Number(detailSt.id) === Number(thermalId)
          ) {
            return detailSt;
          }
          return {
            id: thermalId,
            imgName: this.defectProperties?.img_name || ''
          };
        }

        const selected = this.$store.getters['dashboard/getSelectedThermalImage'];
        if (selected && selected.id != null && selected.id !== '') {
          return selected;
        }

        return null;
      },

      async initializeDefectEditor() {
        const defectId = this.resolvedDefectId;
        if (!defectId) return;

        this.isInitializingEditor = true;
        this.isLoadingMainTypes = true;
        try {
          const thermalId = this.resolveThermalImageIdForPopup();
          const aiInclude = this.$store.state.dashboard?.showAiDefectFlag || false;

          let listDefect = null;
          if (thermalId) {
            const listRes = await moduleDefectApi.getDefectsByThermalImageId(thermalId, aiInclude);
            const content = listRes?.content || [];
            listDefect = findDefectInThermalList(content, defectId);
          }

          const [parentRes, detail] = await Promise.all([
            defectTypeApi.getParentDefectTypes(),
            getModuleDefectDetailCached(defectId)
          ]);
          const rawParents = parentRes?._embedded?.defectTypes;
          this.mainDefectTypes = Array.isArray(rawParents) ? rawParents : [];
          console.log('[DEFECT-MAP-DEBUG] DefectOverlayPopupContent initializeDefectEditor', {
            defectId,
            thermalId,
            fromThermalList: !!listDefect,
            mainDefectTypesCount: this.mainDefectTypes.length,
            parentResKeys: parentRes && typeof parentRes === 'object' ? Object.keys(parentRes) : [],
            hasDetail: !!detail,
            defectPropsKeys:
              this.defectProperties && typeof this.defectProperties === 'object'
                ? Object.keys(this.defectProperties)
                : []
          });
          let detailMerged = mergeHalEmbeddedDefectCode(
            detail && typeof detail === 'object' ? detail : null
          );
          detailMerged = mergeListDefectWithDetail(detailMerged, listDefect);
          const mergedPk = this.normalizeDefectDetailPk(detailMerged, defectId);
          this.defectDetail =
            detailMerged && typeof detailMerged === 'object'
              ? { ...detailMerged, id: mergedPk != null ? mergedPk : defectId }
              : null;
          await this.fetchDefectCodeIfMissing(mergedPk != null ? mergedPk : defectId, {
            alertOnMissingCode: true
          });
          this.localDefectName =
            this.defectDetail?.defectCode?.name ||
            this.defectDetail?.defectType ||
            this.defectProperties.defect_name ||
            this.defectProperties.defectType ||
            '';

          const currentDefectCode = this.defectDetail?.defectCode;
          if (currentDefectCode && !this.localDefectName && currentDefectCode.id) {
            const defectTypeDetail = await this.fetchDefectTypeById(currentDefectCode.id);
            this.localDefectName = defectTypeDetail?.name || this.localDefectName;
          }

          this.selectedMainType = null;
          this.selectedSubType = null;
          this.subDefectTypes = [];

          const wfsTypeCode = this.defectProperties?.defect_type;

          /** 열화상 결함 목록·상세에 parent가 있으면 findByParent_Seq는 parent.id당 1회만 (applyCurrentDefectTypeSelection) */
          let selectionFromRest = false;
          if (
            currentDefectCode &&
            (currentDefectCode.id != null || currentDefectCode.parent != null)
          ) {
            await this.applyCurrentDefectTypeSelection(currentDefectCode);
            selectionFromRest = !!(this.selectedMainType || this.selectedSubType);
          }

          if (!selectionFromRest && wfsTypeCode && !this.isWfsAiDefectType()) {
            const fromWfs = await this.applySelectionFromWfsDefectTypeCode(wfsTypeCode);
            selectionFromRest = fromWfs && !!(this.selectedMainType || this.selectedSubType);
          }

          /** 목록 API(defectCode null) 이후에도 상·하위 셀렉트 초기값 — CELL */
          if (!selectionFromRest && !hasDefectCode(this.defectDetail)) {
            await this.applyCellDefaultSelection();
          }

          this.localDefectName =
            this.selectedSubType?.name || this.selectedMainType?.name || this.localDefectName;
        } catch (error) {
          this.alertError(error);
        } finally {
          this.isLoadingMainTypes = false;
          this.isInitializingEditor = false;
        }
      },
      /**
       * WFS 피처의 defect_type 값(예: CELL_MULTI_MEDIUM)을 DefectType.code와 대소문자 무시로 매칭.
       * 카탈로그 활성 시: 객체로만 매칭.
       * REST에 parent가 없을 때만 사용. 하위 조회는 parent.id당 findByParent_Seq 1회(또는 상위 code 일치 시 1회).
       * 상위 타입마다 반복 호출(findByParent_Seq N회)은 하지 않는다.
       */
      async applySelectionFromWfsDefectTypeCode(wfsCodeRaw) {
        const wfsCode = String(wfsCodeRaw || '').trim();
        if (!wfsCode || !this.mainDefectTypes.length) {
          return false;
        }

        const codeEq = (a, b) =>
          a != null && b != null && String(a).toUpperCase() === String(b).toUpperCase();

        this.isLoadingSubTypes = true;
        try {
          if (isDefectTypesCatalogActive()) {
            const cat = resolveCatalogWfsDefectSelection(wfsCode);
            if (cat) {
              this.selectedMainType =
                this.mainDefectTypes.find(t => t.id === cat.mainType.id) || cat.mainType;
              this.subDefectTypes = cat.subDefectTypes;
              this.selectedSubType = cat.subType;
              return true;
            }
            return false;
          }

          const parentFromDetail = this.defectDetail?.defectCode?.parent;
          const parentId =
            parentFromDetail && parentFromDetail.id != null && parentFromDetail.id !== ''
              ? Number(parentFromDetail.id)
              : null;

          if (Number.isFinite(parentId) && parentId > 0) {
            this.selectedMainType =
              this.mainDefectTypes.find(t => Number(t.id) === parentId) || parentFromDetail;
            this.subDefectTypes = await moduleDefectApi.getChildrenDefectTypes(parentId);
            this.selectedSubType =
              (this.subDefectTypes || []).find(c => codeEq(c.code, wfsCode)) || null;
            return this.selectedSubType != null;
          }

          const mainHit = this.mainDefectTypes.find(t => codeEq(t.code, wfsCode));
          if (mainHit) {
            this.selectedMainType = mainHit;
            this.subDefectTypes = await moduleDefectApi.getChildrenDefectTypes(mainHit.id);
            this.selectedSubType = null;
            return true;
          }
        } finally {
          this.isLoadingSubTypes = false;
        }
        return false;
      },
      /** GET .../solarThermalImageId 응답에 defectCode가 null일 때 상위 CELL */
      async applyCellDefaultSelection() {
        const cellType = findCellParentType(this.mainDefectTypes);
        if (!cellType?.id) {
          return false;
        }
        this.selectedMainType = cellType;
        this.isLoadingSubTypes = true;
        try {
          this.subDefectTypes = await moduleDefectApi.getChildrenDefectTypes(cellType.id);
          this.selectedSubType = null;
          return true;
        } catch (error) {
          console.warn('[DefectOverlayPopupContent] CELL 하위 유형 로드 실패:', error);
          this.subDefectTypes = [];
          return false;
        } finally {
          this.isLoadingSubTypes = false;
        }
      },
      async applyCurrentDefectTypeSelection(currentDefectCode) {
        if (!currentDefectCode?.id) {
          this.subDefectTypes = [];
          this.selectedSubType = null;
          return;
        }

        let code = currentDefectCode;
        if (!code.parent) {
          const full = await this.fetchDefectTypeById(code.id);
          if (full && typeof full === 'object') {
            code = { ...code, ...full };
          }
        }

        const hasParent = !!code?.parent;
        const mainTypeId = hasParent ? code.parent.id : code.id;
        const subTypeId = hasParent ? code.id : null;

        const mainFromList = this.mainDefectTypes.find(
          item => Number(item.id) === Number(mainTypeId)
        );
        this.selectedMainType =
          mainFromList ||
          (hasParent && code.parent && Number(code.parent.id) === Number(mainTypeId)
            ? code.parent
            : null);

        if (!this.selectedMainType) {
          this.subDefectTypes = [];
          this.selectedSubType = null;
          return;
        }

        // 상위만 매칭(예: 기본 CELL) — 하위 id는 없어도 상세유형 목록은 로드해야 선택 UI가 보임
        this.isLoadingSubTypes = true;
        try {
          const parentSeq = Number(mainTypeId);
          this.subDefectTypes = await moduleDefectApi.getChildrenDefectTypes(
            Number.isFinite(parentSeq) ? parentSeq : this.selectedMainType.id
          );
          this.selectedSubType = subTypeId
            ? this.subDefectTypes.find(item => Number(item.id) === Number(subTypeId)) || null
            : null;
        } finally {
          this.isLoadingSubTypes = false;
        }
      },
      async onMainTypeChange(mainType) {
        this.selectedSubType = null;
        if (!mainType?.id) {
          this.subDefectTypes = [];
          return;
        }
        this.isLoadingSubTypes = true;
        try {
          this.subDefectTypes = await moduleDefectApi.getChildrenDefectTypes(mainType.id);
        } catch (error) {
          this.alertError(error);
        } finally {
          this.isLoadingSubTypes = false;
        }
      },
      async saveDefectType() {
        if (this.isSaving) return;
        const ready = await this.ensureDefectDetailForActions();
        if (!ready) return;

        let apiSaveData;
        try {
          apiSaveData = this.buildApiSaveDataLikeDefectInfoCard();
        } catch (error) {
          this.alertError(error);
          return;
        }
        const defectCodeId =
          apiSaveData.defectCode && apiSaveData.defectCode.id ? apiSaveData.defectCode.id : null;
        if (!defectCodeId) {
          this.alertError(new Error(this.$t('defects.popup.selectDefectType')));
          return;
        }
        if (!apiSaveData.defectShape) {
          this.alertError(new Error(this.$t('defects.popup.missingDefectShape')));
          return;
        }

        this.isSaving = true;
        try {
          console.log(
            '[DEFECT-POSITION-DEBUG] 팝업 저장 PUT 요약(defectShape는 defectDetail·캐시 기준이면 지도 이동 후와 불일치 가능)',
            {
              defectId: apiSaveData.id,
              defectShape: apiSaveData.defectShape,
              defectCodeId: apiSaveData.defectCode?.id,
              thermalIdResolved: apiSaveData.solarThermalImage?.id
            }
          );
          await api.update(`/module-defects/${apiSaveData.id}`, apiSaveData, true);
          await this.$store.dispatch('defect/refreshAfterDefectChange', {
            solarThermalImageId:
              apiSaveData.solarThermalImage?.id ||
              this.resolveNestedId(this.defectDetail?.solarThermalImage) ||
              this.resolveNestedId(this.defectDetail?._embedded?.solarThermalImage) ||
              this.solarThermalImageId ||
              this.thermalImage?.id ||
              this.$store.getters['dashboard/getSelectedThermalImage']?.id,
            reportId:
              this.reportId ||
              apiSaveData.solarExamineReport?.id ||
              this.resolveNestedId(this.defectDetail?.solarExamineReport) ||
              this.resolveNestedId(this.defectDetail?._embedded?.solarExamineReport),
            changeType: 'update'
          });
          try {
            await syncDefectModuleLinkFromServerLocation(apiSaveData.id, this.$store, {
              trigger: 'defect-overlay-popup-save',
              thermalImageId:
                apiSaveData.solarThermalImage?.id ||
                this.solarThermalImageId ||
                this.$store.getters['dashboard/getSelectedThermalImageId'],
              defectShape: apiSaveData.defectShape
            });
          } catch (syncError) {
            console.warn('[DEFECT-MAP] 팝업 저장 후 모듈 연결 동기화 실패:', syncError);
          }
          this.$eventBus.$emit('alert-on', {
            on: true,
            message: this.$t('defects.popup.saved'),
            status: 'success'
          });
          const selectedTypeName = this.selectedSubType?.name || this.selectedMainType?.name;
          if (selectedTypeName) {
            this.localDefectName = selectedTypeName;
          }
          setModuleDefectDetailCache(apiSaveData.id, {
            ...this.defectDetail,
            ...apiSaveData,
            defectCode: {
              id: defectCodeId,
              name: selectedTypeName || this.defectDetail?.defectCode?.name
            }
          });
        } catch (error) {
          this.alertError(error);
        } finally {
          this.isSaving = false;
        }
      },
      /**
       * DefectInfoCard.save()의 apiSaveData와 동일한 필드·의미로 구성 (PUT /module-defects/{id}).
       * 결함코드 우선순위: 세부유형 → 유형 → 상세에 로드된 defectCode (모달의 sub→main→… 와 동일).
       * 열화상/보고서 id: 기존 결함 수정 시 defectDetail에 묶인 열화상 우선(탐색기 포커스와 무관), 없으면 store·보강.
       * 온도: 모달은 defectTemperature만 쓰지만, 지도 팝업에서는 상세 값으로 폴백.
       */
      buildApiSaveDataLikeDefectInfoCard() {
        const dashboardThermal = this.$store.getters['dashboard/getSelectedThermalImage'] || null;
        const thermal = this.thermalImage || dashboardThermal || null;

        const reportIdResolved =
          this.reportId ||
          this.resolveNestedId(this.defectDetail?.solarExamineReport) ||
          this.resolveNestedId(this.defectDetail?._embedded?.solarExamineReport) ||
          null;

        const thermalSeqRaw = this.defectProperties?.thermal_img_seq;
        const thermalFromSeq =
          thermalSeqRaw != null && thermalSeqRaw !== '' ? Number(thermalSeqRaw) : null;
        const thermalFromDefect =
          this.resolveNestedId(this.defectDetail?.solarThermalImage) ||
          this.resolveNestedId(this.defectDetail?._embedded?.solarThermalImage) ||
          null;
        const thermalIdResolved =
          thermalFromDefect ||
          thermal?.id ||
          (Number.isFinite(thermalFromSeq) && thermalFromSeq > 0 ? thermalFromSeq : null);

        const memoForApi = this.defectDetail?.defectDesc || this.defectDetail?.memo || '';

        const defectCodeId =
          this.selectedSubType != null && this.selectedSubType.id != null
            ? this.selectedSubType.id
            : this.selectedMainType != null && this.selectedMainType.id != null
              ? this.selectedMainType.id
              : this.defectDetail?.defectCode?.id != null
                ? this.defectDetail.defectCode.id
                : null;

        const temp = this.defectTemperature || {};
        const apiSaveData = {
          id: this.normalizeDefectDetailPk(this.defectDetail, this.resolvedDefectId),
          detectWay: this.defectDetail.detectWay || 'MANUAL',
          defectStatus: this.defectDetail.defectStatus,
          defectDesc: memoForApi,
          memo: memoForApi,
          solarThermalImage: { id: thermalIdResolved },
          solarExamineReport: { id: reportIdResolved },
          defectShape:
            this.defectDetail.defectShape ||
            geomToPanelDefectShape(this.defectDetail.defectShapeGeom),
          maxHeat: temp.max != null ? temp.max : this.defectDetail.maxHeat,
          minHeat: temp.min != null ? temp.min : this.defectDetail.minHeat,
          averageHeat: temp.avr != null ? temp.avr : this.defectDetail.averageHeat,
          mlDefectConfirmed: this.defectDetail.mlDefectConfirmed
        };

        if (
          this.selectedSubType ||
          this.selectedMainType ||
          (this.defectDetail && this.defectDetail.defectCode)
        ) {
          if (defectCodeId != null && defectCodeId !== '') {
            const nid = Number(defectCodeId);
            apiSaveData.defectCode = {
              id: Number.isFinite(nid) ? nid : defectCodeId
            };
          }
        }

        if (apiSaveData.detectWay === 'AI' && apiSaveData.mlDefectConfirmed === 'NO') {
          apiSaveData.mlDefectConfirmed = 'YES';
        }

        return apiSaveData;
      },
      async deleteDefect() {
        if (this.isDeleting) return;
        const ready = await this.ensureDefectDetailForActions();
        if (!ready) return;

        this.isDeleting = true;
        try {
          const deletedDefectId = this.normalizeDefectDetailPk(
            this.defectDetail,
            this.resolvedDefectId
          );
          if (deletedDefectId == null) {
            this.alertError(new Error(this.$t('defects.popup.loadDefectFailed')));
            return;
          }

          const defectForDelete = {
            ...this.defectDetail,
            seq: deletedDefectId,
            id: deletedDefectId
          };

          await optimisticDeleteDefect(this.$store, defectForDelete, {
            eventBus: this.$eventBus,
            successMessage: this.$t('defects.popup.deleted'),
            reportId:
              this.reportId ||
              this.resolveNestedId(this.defectDetail?.solarExamineReport) ||
              this.resolveNestedId(this.defectDetail?._embedded?.solarExamineReport)
          });

          await this.$store.dispatch('defect/refreshAfterDefectChange', {
            solarThermalImageId:
              this.resolveNestedId(this.defectDetail?.solarThermalImage) ||
              this.resolveNestedId(this.defectDetail?._embedded?.solarThermalImage) ||
              this.solarThermalImageId ||
              this.thermalImage?.id ||
              this.$store.getters['dashboard/getSelectedThermalImage']?.id,
            reportId:
              this.reportId ||
              this.resolveNestedId(this.defectDetail?.solarExamineReport) ||
              this.resolveNestedId(this.defectDetail?._embedded?.solarExamineReport),
            changeType: 'delete'
          });
          this.$emit('close-popup');
        } catch (error) {
          this.alertError(error);
        } finally {
          this.isDeleting = false;
        }
      },
      openDefectList() {
        const thermalImage = this.resolveThermalImageForOpenDefectList();
        if (!thermalImage || thermalImage.id == null || thermalImage.id === '') {
          this.alertError(new Error(this.$t('defects.popup.openDefectListNoThermal')));
          return;
        }
        this.$store.dispatch('defect/openDefectList', {
          thermalImage,
          focusDefectId: this.resolvedDefectId
        });
        this.$emit('close-popup');
      },
      openDetail() {
        const defectId = Number(this.resolvedDefectId);
        if (!Number.isFinite(defectId) || defectId <= 0) {
          this.alertError(new Error(this.$t('defects.popup.noDefectSeq')));
          return;
        }
        this.$emit('open-detail', {
          defectId,
          thermalImgSeq: this.resolveThermalImageIdForPopup()
        });
      },
      alertError(error) {
        this.$eventBus.$emit('alert-on', {
          on: true,
          message: error?.message || this.$t('defects.popup.requestFailed'),
          status: 'error'
        });
      },
      /** WFS 피처의 defect_type이 "ai"인 경우 서버에 defectCode가 없을 수 있어 별도 조회를 하지 않는다. */
      isWfsAiDefectType() {
        const t = this.defectProperties?.defect_type;
        if (t == null || t === '') {
          return false;
        }
        return String(t).trim().toLowerCase() === 'ai';
      },
      async fetchDefectTypeById(defectTypeId) {
        if (!defectTypeId) return null;
        return moduleDefectApi.getDefectTypeById(defectTypeId);
      },
      /**
       * HAL에 defectCode 본문이 없을 때 GET /moduleDefects/{id}/defectCode 로 보강.
       * - defect_type이 "ai"이면 요청하지 않음(AI 결함은 서버에 유형이 없을 수 있음).
       * - ai가 아닌데 404·빈 응답이면 alertOnMissingCode 시 기존 alert-on으로 안내.
       */
      async fetchDefectCodeIfMissing(moduleDefectPk, options = {}) {
        const { alertOnMissingCode = false } = options;
        if (!moduleDefectPk || !this.defectDetail) {
          return;
        }
        const existing = this.defectDetail.defectCode;
        if (existing && (existing.id != null || existing.seq != null)) {
          return;
        }
        if (this.isWfsAiDefectType()) {
          return;
        }
        try {
          const response = await api.read(`/moduleDefects/${moduleDefectPk}/defectCode`);
          const code = response?.data;
          const hasPayload =
            code && typeof code === 'object' && (code.id != null || code.seq != null);
          if (hasPayload) {
            this.defectDetail = { ...this.defectDetail, defectCode: code };
          } else if (alertOnMissingCode) {
            this.alertError(new Error(this.$t('defects.popup.missingDefectCodeOnServer')));
          }
        } catch (error) {
          const status = error?.response?.status;
          if (status === 404) {
            if (alertOnMissingCode) {
              this.alertError(new Error(this.$t('defects.popup.missingDefectCodeOnServer')));
            }
          } else {
            this.alertError(error);
          }
        }
      },
      /**
       * 저장/삭제 클릭 시 결함 상세가 없거나 id 불일치면 API로 다시 로드한다.
       * 버튼은 비활성화하지 않고, 여기서 검증·안내한다.
       */
      normalizeDefectDetailPk(detail, fallbackPk) {
        if (!detail || typeof detail !== 'object') {
          const fb = Number(fallbackPk);
          return Number.isFinite(fb) && fb > 0 ? fb : null;
        }
        const fromHal = extractModuleDefectPkFromHalLinks(detail);
        if (fromHal != null) {
          return fromHal;
        }
        const candidates = [
          detail.id,
          detail.seq,
          detail.pk,
          detail.defectId,
          detail.moduleDefectSeq,
          detail.moduleDefectId
        ];
        for (let i = 0; i < candidates.length; i += 1) {
          const n = Number(candidates[i]);
          if (Number.isFinite(n) && n > 0) {
            return n;
          }
        }
        const fb = Number(fallbackPk);
        return Number.isFinite(fb) && fb > 0 ? fb : null;
      },
      resolveNestedId(value) {
        if (value == null) {
          return null;
        }
        if (typeof value === 'number' && Number.isFinite(value)) {
          return value;
        }
        if (typeof value === 'object' && value.id != null) {
          return value.id;
        }
        if (typeof value === 'object' && value.seq != null) {
          return Number(value.seq);
        }
        return null;
      },
      async ensureDefectDetailForActions() {
        const defectId = this.resolvedDefectId;
        if (!defectId) {
          this.alertError(new Error(this.$t('defects.popup.noDefectSeq')));
          return false;
        }
        const currentPk = this.normalizeDefectDetailPk(this.defectDetail, defectId);
        if (currentPk != null && currentPk === defectId) {
          return true;
        }
        try {
          const detailRaw = await getModuleDefectDetailCached(defectId);
          const detail = mergeHalEmbeddedDefectCode(
            detailRaw && typeof detailRaw === 'object' ? detailRaw : null
          );
          const loadedPk = this.normalizeDefectDetailPk(detail, defectId);
          if (!detail || loadedPk == null) {
            this.alertError(new Error(this.$t('defects.popup.loadDefectFailed')));
            return false;
          }
          this.defectDetail =
            detail && typeof detail === 'object' ? { ...detail, id: loadedPk } : detail;
          await this.fetchDefectCodeIfMissing(loadedPk, {
            alertOnMissingCode: false
          });
          return true;
        } catch (error) {
          this.alertError(error);
          return false;
        }
      }
    }
  };
</script>

<style scoped>
  .map-popup-content-root {
    font-size: 13px;
    position: relative;
    z-index: 1;
    color: var(--v-text-base);
    /* 이미지(~433px)가 팝업 폭을 잡으므로 하단도 100%로 맞춤 — max-width 고정 시 우측 공백 */
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .popup-defect-header {
    margin-bottom: 6px;
    gap: 8px;
  }

  .popup-defect-id {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
  }

  .popup-defect-id__label {
    font-size: 0.6875rem;
    font-weight: 500;
    color: var(--v-secondary-base);
    flex-shrink: 0;
  }

  .popup-defect-id__value {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--v-text-base);
    letter-spacing: 0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* 2열 그리드 — Array|String / Module|상태 / 이미지 전체폭 */
  .popup-meta-block {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px 12px;
    padding: 6px 0;
    margin-bottom: 2px;
    border-top: 1px solid var(--v-borderSubtle-base);
    border-bottom: 1px solid var(--v-borderSubtle-base);
  }

  .popup-meta-row {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
  }

  .popup-meta-row--full {
    grid-column: 1 / -1;
  }

  .popup-meta-label {
    font-size: 0.625rem;
    font-weight: 500;
    color: var(--v-secondary-base);
    line-height: 1.25;
    flex-shrink: 0;
  }

  .popup-meta-value {
    font-size: 0.75rem;
    font-weight: 400;
    color: var(--v-text-base);
    line-height: 1.25;
    word-break: break-word;
    text-align: right;
    min-width: 0;
  }

  .popup-meta-value--file {
    font-variant-numeric: tabular-nums;
  }

  .popup-edit-block {
    padding: 6px 0 0;
  }

  .popup-defect-select :deep(.v-input__control) {
    background-color: var(--v-surface-base);
  }

  .popup-defect-select :deep(.v-input__slot) {
    min-height: 32px !important;
  }

  /* outlined 테두리: 다크 surface에서 기본 borderSubtle이 거의 안 보임 → 대비 상향 */
  .popup-defect-select :deep(fieldset) {
    border-width: 1.5px !important;
    border-color: color-mix(in srgb, var(--v-text-base) 38%, transparent) !important;
  }

  .popup-defect-select :deep(.v-input--is-focused fieldset) {
    border-color: var(--v-primary-base) !important;
    border-width: 2px !important;
  }

  .popup-defect-select :deep(.v-label),
  .popup-defect-select :deep(.v-select__selection),
  .popup-defect-select :deep(.v-select__selection--comma),
  .popup-defect-select :deep(.v-input__append-inner .v-icon) {
    color: var(--v-text-base) !important;
    font-size: 12px !important;
  }

  /* 비활성·placeholder: Vuetify 기본 opacity로 다크에서 사라짐 → 대비 복구 */
  .popup-defect-select :deep(.v-input--is-disabled) {
    opacity: 1 !important;
  }

  .popup-defect-select :deep(.v-input--is-disabled .v-input__slot) {
    background-color: color-mix(in srgb, var(--v-surface-base) 88%, var(--v-text-base)) !important;
  }

  .popup-defect-select :deep(.v-input--is-disabled fieldset) {
    border-color: color-mix(in srgb, var(--v-text-base) 28%, transparent) !important;
  }

  .popup-defect-select :deep(.v-input--is-disabled .v-label),
  .popup-defect-select :deep(.v-input--is-disabled .v-select__selections),
  .popup-defect-select :deep(.v-input--is-disabled input),
  .popup-defect-select :deep(.v-input--is-disabled .v-icon) {
    color: color-mix(in srgb, var(--v-text-base) 62%, transparent) !important;
  }

  .popup-defect-select :deep(input::placeholder),
  .popup-defect-select :deep(.v-select__selections input::placeholder) {
    color: color-mix(in srgb, var(--v-text-base) 62%, transparent) !important;
    opacity: 1 !important;
  }

  .popup-action-row {
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px solid var(--v-borderSubtle-base);
    gap: 8px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  .popup-action-btn {
    flex: 1 1 0;
    max-width: 100%;
    min-width: 0 !important;
    width: auto !important;
    min-height: 36px !important;
    height: 36px !important;
    font-size: 13px !important;
    font-weight: 600;
  }

  .popup-defect-header .popup-action-btn {
    flex: 0 0 auto;
  }

  .module-defect-list {
    max-height: 220px;
    overflow-y: auto;
    background-color: color-mix(in srgb, var(--v-surface-base) 60%, var(--v-text-base) 2%);
    border-radius: 6px;
    border: 1px solid var(--v-borderSubtle-base);
    padding: 0;
  }

  .module-defect-item {
    border-bottom: 1px solid color-mix(in srgb, var(--v-borderSubtle-base) 150%, transparent);
    cursor: pointer;
    transition: background-color 0.12s ease;
  }

  .module-defect-item:last-child {
    border-bottom: none;
  }

  .module-defect-item:hover {
    background-color: color-mix(in srgb, var(--v-primary-base) 8%, var(--v-surface-base));
  }

  .module-defect-item:active {
    background-color: color-mix(in srgb, var(--v-primary-base) 14%, var(--v-surface-base));
  }

  .module-defect-delete-btn {
    opacity: 0.5;
    transition:
      opacity 0.15s ease,
      border-color 0.15s ease;
    border: 1px solid transparent;
    border-radius: 4px;
    width: 26px !important;
    height: 26px !important;
  }

  .module-defect-delete-btn:hover {
    opacity: 1;
    border-color: color-mix(in srgb, var(--v-error-base) 40%, transparent);
  }

  .module-defect-delete-btn .v-icon {
    color: var(--v-text-base);
    transition: color 0.12s ease;
  }

  .module-defect-delete-btn:hover .v-icon {
    color: var(--v-error-base) !important;
  }

  .module-defect-icon {
    object-fit: contain;
  }
</style>
